import { useEffect, useState } from "react"
import { api, ApiError, type ApiTokenSummary } from "./api"

export function TokensView() {
    const [tokens, setTokens] = useState<ApiTokenSummary[]>([])
    const [label, setLabel] = useState("")
    const [justIssued, setJustIssued] = useState<string | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    async function load() {
        try {
            setTokens(await api.listTokens())
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to load tokens")
        }
    }

    useEffect(() => {
        load()
    }, [])

    async function handleCreate(e: React.FormEvent) {
        e.preventDefault()
        setBusy(true)
        setError(null)
        try {
            const { token } = await api.createToken(label)
            setJustIssued(token)
            setLabel("")
            await load()
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to create token")
        } finally {
            setBusy(false)
        }
    }

    async function handleRevoke(id: string) {
        setBusy(true)
        try {
            await api.revokeToken(id)
            await load()
        } catch (err) {
            setError(err instanceof ApiError ? err.message : "Failed to revoke token")
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="tokens-view">
            <section className="submit-panel">
                <h2>Issue a new API token</h2>
                <form onSubmit={handleCreate}>
                    <input
                        type="text"
                        placeholder="Label (optional, e.g. 'acme-dealer-integration')"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                    />
                    <button type="submit" disabled={busy}>
                        Issue token
                    </button>
                </form>
                {error && <p className="error">{error}</p>}
                {justIssued && (
                    <div className="token-reveal">
                        <p>
                            <strong>Copy this now — it will not be shown again.</strong>
                        </p>
                        <code>{justIssued}</code>
                        <button onClick={() => navigator.clipboard.writeText(justIssued)}>Copy</button>
                        <button className="link-button" onClick={() => setJustIssued(null)}>
                            Dismiss
                        </button>
                    </div>
                )}
            </section>

            <section>
                <h2>Issued tokens</h2>
                {tokens.length === 0 ? (
                    <p className="muted">No tokens issued yet.</p>
                ) : (
                    <table>
                        <thead>
                            <tr>
                                <th>Label</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Last used</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tokens.map((t) => (
                                <tr key={t.id}>
                                    <td>{t.label ?? <span className="muted">—</span>}</td>
                                    <td>
                                        <span className={`badge ${t.revoked ? "status-bad" : "status-good"}`}>
                                            {t.revoked ? "Revoked" : "Active"}
                                        </span>
                                    </td>
                                    <td className="mono">{new Date(t.createdAt).toLocaleString()}</td>
                                    <td className="mono">
                                        {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString() : "Never"}
                                    </td>
                                    <td>
                                        {!t.revoked && (
                                            <button disabled={busy} onClick={() => handleRevoke(t.id)}>
                                                Revoke
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    )
}
